import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPhone, FiMail, FiMapPin, FiClock } = FiIcons;

const Contact = () => {
  React.useEffect(() => {
    document.title = 'Contact Us - Royal Hooka Lounge | Location, Hours & Reservations';
  }, []);

  return (
    <div className="pt-32">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-black">
        <div className="container mx-auto px-4">
          <motion.h1 
            className="text-5xl font-serif font-bold text-center text-gold-400 mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Contact Us
          </motion.h1>
          <motion.p 
            className="text-xl text-center text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Get in touch with us for reservations or inquiries
          </motion.p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-serif font-bold text-gold-400 mb-8">Get In Touch</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiMapPin} className="w-6 h-6 text-gold-400 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Address</h3>
                    <p className="text-gray-300">123 Luxury Street<br />Downtown City, DC 12345</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiPhone} className="w-6 h-6 text-gold-400 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Phone</h3>
                    <p className="text-gray-300">(555) 123-4567</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiMail} className="w-6 h-6 text-gold-400 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Email</h3>
                    <p className="text-gray-300">info@royalhooka.com</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiClock} className="w-6 h-6 text-gold-400 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Hours</h3>
                    <p className="text-gray-300">Open 24/7<br />Every day of the week</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-gray-900 p-8 rounded-lg border border-gold-500/20"
            >
              <h2 className="text-2xl font-serif font-bold text-gold-400 mb-6">Send us a Message</h2>
              <form className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 bg-black border border-gold-500/30 rounded-lg text-white focus:border-gold-400 focus:outline-none"
                    placeholder="Your Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <input 
                    type="email" 
                    className="w-full px-4 py-3 bg-black border border-gold-500/30 rounded-lg text-white focus:border-gold-400 focus:outline-none"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                  <input 
                    type="tel" 
                    className="w-full px-4 py-3 bg-black border border-gold-500/30 rounded-lg text-white focus:border-gold-400 focus:outline-none"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                  <textarea 
                    rows="4"
                    className="w-full px-4 py-3 bg-black border border-gold-500/30 rounded-lg text-white focus:border-gold-400 focus:outline-none"
                    placeholder="Your message..."
                  ></textarea>
                </div>
                <button 
                  type="submit"
                  className="w-full bg-gradient-to-r from-gold-400 to-gold-600 text-black px-6 py-3 rounded-lg font-semibold hover:from-gold-500 hover:to-gold-700 transition-all duration-300"
                >
                  Send Message
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <motion.h2 
            className="text-4xl font-serif font-bold text-center text-gold-400 mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Find Us
          </motion.h2>
          <motion.div 
            className="bg-gray-800 h-96 rounded-lg flex items-center justify-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="text-center">
              <SafeIcon icon={FiMapPin} className="w-16 h-16 text-gold-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Interactive Map</h3>
              <p className="text-gray-300">Map integration would be implemented here</p>
              <p className="text-gray-400 text-sm mt-2">123 Luxury Street, Downtown City, DC 12345</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;